/**
 * The right side of a follower's row: its first own setting ("amount 802")
 * and "+N" for the rest — the value itself says it is the container's own.
 * Hovering shows a small card with every own setting and where the rest
 * comes from.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { Tooltip } from '@/components/ui';

import { type OwnSetting, ownSummary } from './rule-facts';

interface Props {
  own: OwnSetting[];
  masterName: string;
}

const Card: FC<Props> = ({ own, masterName }) => (
  <span className="flex flex-col gap-2">
    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Own settings</span>
    <span className="grid grid-cols-[auto_auto] gap-x-6 gap-y-1">
      {own.map((o) => (
        <span key={o.key} className="contents">
          <span className="text-muted-foreground">{o.label}</span>
          <span className={o.value ? 'text-right font-medium tabular-nums' : 'text-right text-muted-foreground'}>{o.value ?? 'its own'}</span>
        </span>
      ))}
    </span>
    <span className="border-t border-border pt-2 text-muted-foreground">Everything else comes from {masterName}.</span>
  </span>
);

export const OwnSettingsHint: FC<Props> = ({ own, masterName }) =>
  own.length === 0 ? null : (
    <Tooltip
      content={<Card own={own} masterName={masterName} />}
      contentClassName="min-w-[220px] border border-border bg-popover px-3 py-2.5 text-popover-foreground"
    >
      <span className="inline-flex cursor-default items-center gap-1.5 text-[11px] text-muted-foreground tabular-nums">
        {ownSummary(own)}
        {own.length > 1 ? <span className="rounded bg-muted px-1 text-[10px] font-medium">+{own.length - 1}</span> : null}
      </span>
    </Tooltip>
  );
